import { Fragment, useState, useEffect } from 'react'
import { Dialog, Menu, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  BellIcon,
  HomeIcon,
  XMarkIcon,
  TruckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { getInvoicesForCustomer, getShipmentsForCustomer } from '@/services/firebase'
import { COMPANY } from '@/config/company'
import logo from '@/assets/logo.png'

const navigation = [
  { name: 'Dashboard', href: '/portal/dashboard', icon: HomeIcon },
  { name: 'My Shipments', href: '/portal/shipments', icon: TruckIcon },
  { name: 'My Invoices', href: '/portal/invoices', icon: DocumentTextIcon },
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface Alert {
  id: string
  title: string
  detail: string
  href: string
  tone: 'due' | 'moving'
}

export default function CustomerLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [alerts, setAlerts] = useState<Alert[]>([])
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  // The bell used to be a button with no handler. These are the two things a
  // customer actually needs to know about: money owed and packages moving.
  useEffect(() => {
    if (!user) return

    const loadAlerts = async () => {
      try {
        const [invoices, shipments] = await Promise.all([
          getInvoicesForCustomer(user.id),
          getShipmentsForCustomer(user.id),
        ])

        const unpaid: Alert[] = invoices
          .filter((inv) => inv.status !== 'paid' && inv.status !== 'cancelled')
          .slice(0, 5)
          .map((inv) => ({
            id: `inv-${inv.id}`,
            title: `Invoice #${inv.invoiceNumber} is awaiting payment`,
            detail: new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: inv.currency || 'USD',
            }).format(inv.total || 0),
            href: `/portal/invoices/${inv.id}`,
            tone: 'due' as const,
          }))

        const moving: Alert[] = shipments
          .filter((s) => s.status === 'in_transit')
          .slice(0, 5)
          .map((s) => ({
            id: `shp-${s.id}`,
            title: `Shipment ${s.trackingNumber} is in transit`,
            detail: 'On its way to you',
            href: `/portal/shipments/${s.id}`,
            tone: 'moving' as const,
          }))

        setAlerts([...unpaid, ...moving])
      } catch (err) {
        console.error('Could not load notifications:', err)
        setAlerts([])
      }
    }

    loadAlerts()
  }, [user])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    navigate(q ? `/portal/shipments?q=${encodeURIComponent(q)}` : '/portal/shipments')
  }

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 md:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-pds-dark px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center gap-3">
                        <img src={logo} alt="" className="h-8 w-8 rounded" />
                        <span className="text-white font-semibold">{COMPANY.shortName}</span>
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                <NavLink
                                  to={item.href}
                                  className={({ isActive }) =>
                                    classNames(
                                      isActive
                                        ? 'bg-pds-blue text-white'
                                        : 'text-gray-400 hover:text-white hover:bg-pds-blue/50',
                                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                    )
                                  }
                                >
                                  <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                                  {item.name}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li className="mt-auto">
                           <div className="text-center text-xs text-pds-silver">
                            <p>{COMPANY.email}</p>
                            <p>&copy; {new Date().getFullYear()} {COMPANY.name}</p>
                          </div>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden md:fixed md:inset-y-0 md:z-50 md:flex md:w-72 md:flex-col">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-pds-dark px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center gap-3">
                  <img src={logo} alt="" className="h-9 w-9 rounded" />
                  <span className="text-white font-semibold text-lg">{COMPANY.shortName}</span>
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <NavLink
                          to={item.href}
                          className={({ isActive }) =>
                            classNames(
                              isActive
                                ? 'bg-pds-blue text-white'
                                : 'text-gray-400 hover:text-white hover:bg-pds-blue/50',
                              'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                            )
                          }
                        >
                          <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                          {item.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto">
                  <div className="text-center text-xs text-pds-silver">
                    <p>{COMPANY.email}</p>
                    <p>&copy; {new Date().getFullYear()} {COMPANY.name}</p>
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="md:pl-72">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button type="button" className="-m-2.5 p-2.5 text-gray-700 md:hidden" onClick={() => setSidebarOpen(true)}>
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-900/10 lg:hidden" aria-hidden="true" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <form className="relative flex flex-1" onSubmit={handleSearch} role="search">
                <label htmlFor="search-field" className="sr-only">
                  Search your shipments
                </label>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="search-field"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search your shipments by tracking number or item…"
                  type="search"
                  name="search"
                />
              </form>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* Notifications */}
                <Menu as="div" className="relative">
                  <Menu.Button className="relative -m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                    <span className="sr-only">
                      View notifications{alerts.length ? ` (${alerts.length})` : ''}
                    </span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                    {alerts.length > 0 && (
                      <span className="absolute right-1 top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {alerts.length > 9 ? '9+' : alerts.length}
                      </span>
                    )}
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-80 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      <div className="border-b border-gray-100 px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">Notifications</p>
                      </div>
                      {alerts.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-gray-500">
                          You&apos;re all caught up.
                        </p>
                      ) : (
                        <div className="max-h-80 overflow-y-auto">
                          {alerts.map((alert) => (
                            <Menu.Item key={alert.id}>
                              {({ active }) => (
                                <Link
                                  to={alert.href}
                                  className={classNames(
                                    active ? 'bg-gray-50' : '',
                                    'flex gap-3 border-b border-gray-50 px-4 py-3 last:border-0'
                                  )}
                                >
                                  <span
                                    className={classNames(
                                      'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                                      alert.tone === 'due' ? 'bg-amber-500' : 'bg-blue-500'
                                    )}
                                  />
                                  <span className="min-w-0">
                                    <span className="block text-sm font-medium text-gray-900">
                                      {alert.title}
                                    </span>
                                    <span className="block text-xs text-gray-500">
                                      {alert.detail}
                                    </span>
                                  </span>
                                </Link>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      )}
                    </Menu.Items>
                  </Transition>
                </Menu>

                {/* Separator */}
                <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    {user?.photoURL ? (
                      <img
                        className="h-8 w-8 rounded-full bg-gray-50"
                        src={user.photoURL}
                        alt=""
                      />
                    ) : (
                       <span className="inline-block h-8 w-8 overflow-hidden rounded-full bg-gray-100">
                        <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </span>
                    )}
                    <span className="hidden lg:flex lg:items-center">
                      <span className="ml-4 text-sm font-semibold leading-6 text-gray-900" aria-hidden="true">
                        {user?.name}
                      </span>
                      <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={classNames(active ? 'bg-gray-50' : '', 'block px-3 py-1 text-sm leading-6 text-gray-900')}
                          >
                            Your Profile
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={logout}
                            className={classNames(active ? 'bg-gray-50' : '', 'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900')}
                          >
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <main className="py-10">
            <div className="px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  )
} 